/**
 * Board Dashboard — AML/CTF Compliance Oversight for Board
 * Fetches data from /api/governance/board-dashboard (real tables)
 * Uses venue names from venues table; board signatories from tenant staff when available.
 */
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import BoardDashboardPack from './BoardDashboardPack'
import {
  Shield, FileText, Users, Award, UserX, BookOpen, Building2,
  Activity, Clock, TrendingUp, TrendingDown, Minus, AlertCircle,
  CheckCircle2, AlertTriangle, Banknote, HelpCircle, UserCog
} from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useGovernanceStats, cddTierCount, riskOpenCount, ttrOpenCount } from '../../hooks/useGovernanceStats'

const F = { sans: "'Poppins', system-ui, sans-serif", mono: "'Poppins', system-ui, sans-serif" }
const C = {
  pri: '#0a5169', priD: '#07384a', priL: '#1a7a9b', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  orange: '#ea580c',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc',
  ink: '#0f172a', border: '#e2e8f0',
}

/* Loading / error fallbacks only — no fictional venue or patron tiles */
const EMPTY_VENUE_LIST = []

const PROGRAM_HEALTH = [
  // Static layout demo until board obligations are read from the governance API; dates are not tied to “today”.
  { obligation: 'AML/CTF Program — Part A (Procedures)', status: 'compliant', lastReview: 'Jan 2026', nextDue: 'Jan 2027', evidence: 'system' },
  { obligation: 'AML/CTF Program — Part B (KYC)', status: 'compliant', lastReview: 'Jan 2026', nextDue: 'Jan 2027', evidence: 'system' },
  { obligation: 'Independent Review', status: 'due-soon', lastReview: 'Mar 2025', nextDue: 'Mar 2026', evidence: 'uploaded' },
  { obligation: 'Board Risk Appetite Statement', status: 'compliant', lastReview: 'Dec 2025', nextDue: 'Dec 2026', evidence: 'uploaded' },
  { obligation: 'AUSTRAC Registration', status: 'compliant', lastReview: 'Ongoing', nextDue: 'Ongoing', evidence: 'system' },
  { obligation: 'Correspondent Banking Prohibition', status: 'compliant', lastReview: 'Ongoing', nextDue: 'Ongoing', evidence: 'system' },
]

/* Compliance goals — targets for "good" and "improving" */
const GOALS = {
  smrOnTimePct: 95,
  smrAvgDaysMax: 3,
  stalledCasesMax: 0,
  trainingPct: 95,
  eddPct: 100,
  highRiskWithoutEcddMax: 0,
}

const DEFAULT_SMR = { filedThisQuarter: 0, filedOnTime: 0, filedLate: 0, avgDaysToFile: 0, currentlyActive: 0, overdue: 0, quarterlyTrend: [], lateDetail: null }
const DEFAULT_INV = { openCases: 0, avgAgeOpenDays: 0, stalledCases: 0, closedThisQuarter: 0, avgDaysToClose: 0, escalatedToSMR: 0, dismissedNoAction: 0, stageSplit: [], stalledDetail: null, recentCases: [] }
const EMPTY_PATRON = {
  totalActive: 0,
  riskBands: [
    { label: 'Low (0–39)', count: 0, pct: 0, color: C.green },
    { label: 'Medium (40–69)', count: 0, pct: 0, color: C.amber },
    { label: 'High (70–89)', count: 0, pct: 0, color: C.orange },
    { label: 'Critical (90+)', count: 0, pct: 0, color: C.red },
  ],
  highRiskWithoutECDD: 0,
  venueInitiatedExclusions: { total: 0, thisQuarter: 0, list: [] },
  selfExclusions: { total: 0, thisQuarter: 0 },
  watchlistTotal: 0,
}
/** Live board API only — no fixed 91% placeholder (see BoardDashboardController.getTrainingStats). */
const EMPTY_TRAINING = { overallCompliance: 0, venues: [], overdueBreakdown: [], nextDueWithin30Days: 0 }

function trainingPayloadFromApi(data) {
  if (data?.training != null) return data.training
  const venues = Array.isArray(data?.venues) && data.venues.length > 0 ? data.venues : EMPTY_VENUE_LIST
  return {
    overallCompliance: 0,
    venues: venues.map((v) => ({
      name: v.name,
      required: 0,
      completed: 0,
      overdue: 0,
      pct: 0,
    })),
    overdueBreakdown: [],
    nextDueWithin30Days: 0,
  }
}
/** Empty EDD when API omits `edd` — no fictional staff buckets */
const EMPTY_EDD = { totalStaff: 0, eddCompleted: 0, eddPct: 0, overdue: 0, expiringSoon: 0, byRole: [] }
const EMPTY_VENUE_RISK = []

/* ══════════════════════════════════════════════════════
   SHARED COMPONENTS
══════════════════════════════════════════════════════ */
const RAG = {
  red: { color: C.red, bg: C.redBg, bd: C.redBd, label: 'Requires attention' },
  amber: { color: C.amber, bg: C.amberBg, bd: C.amberBd, label: 'Monitor closely' },
  green: { color: C.green, bg: C.greenBg, bd: C.greenBd, label: 'Compliant' },
}

function RagDot({ status, size = 10 }) {
  const c = status === 'red' ? C.red : status === 'amber' ? C.amber : C.green
  return <div style={{ width: size, height: size, borderRadius: '50%', background: c, flexShrink: 0, boxShadow: `0 0 0 3px ${c}22` }} />
}

function RagBadge({ status, label }) {
  const r = RAG[status]
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: r.color, background: r.bg, border: `1px solid ${r.bd}`, padding: '2px 9px', borderRadius: 20, fontFamily: F.sans }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: r.color }} />
      {label || r.label}
    </span>
  )
}

function SectionCard({ title, subtitle, rag, icon: Icon, children, span = 1 }) {
  const ragColor = rag === 'red' ? C.red : rag === 'amber' ? C.amber : C.green
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderTop: `3px solid ${ragColor}`, borderRadius: 10, gridColumn: `span ${span}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 20px 12px', display: 'flex', gap: 10, alignItems: 'flex-start', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: ragColor + '14', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} color={ragColor} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, fontFamily: F.sans }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: C.slateL, marginTop: 1, fontFamily: F.sans }}>{subtitle}</div>}
        </div>
        <RagBadge status={rag} />
      </div>
      <div style={{ padding: '14px 20px', flex: 1 }}>{children}</div>
    </div>
  )
}

function Stat({ label, value, color = C.ink, sub, size = 28 }) {
  return (
    <div>
      <div style={{ fontSize: size, fontWeight: 900, color, fontFamily: F.sans, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: C.slateL, marginTop: 3, lineHeight: 1.3, fontFamily: F.sans }}>{label}</div>
      {sub && <div style={{ fontSize: 12, fontWeight: 600, color, marginTop: 1, fontFamily: F.sans }}>{sub}</div>}
    </div>
  )
}

function ProgressBar({ pct, color, height = 6 }) {
  return (
    <div style={{ height, background: C.border, borderRadius: height, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: height, transition: 'width 0.3s' }} />
    </div>
  )
}

function ProgramHealth() {
  const hasIssue = PROGRAM_HEALTH.some(o => o.status !== 'compliant')
  const rag = hasIssue ? 'amber' : 'green'
  return (
    <SectionCard
      title="AML/CTF Program Obligations"
      subtitle="Illustrative rows — not live register data; wire to governance obligations API for production"
      rag={rag}
      icon={Shield}
      span={2}
    >
      <div>
        {PROGRAM_HEALTH.map((o, i) => {
          const s = o.status === 'compliant' ? 'green' : o.status === 'due-soon' ? 'amber' : 'red'
          return (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 0', borderBottom: i < PROGRAM_HEALTH.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <RagDot status={s} size={8} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, fontFamily: F.sans }}>{o.obligation}</div>
                <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>Last reviewed: {o.lastReview} · Next due: {o.nextDue}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <RagBadge status={s} label={o.status === 'due-soon' ? 'Due soon' : 'Compliant'} />
                {o.evidence === 'system' && <div style={{ fontSize: 12, color: C.slateL, marginTop: 3, fontFamily: F.sans }}>System-verified</div>}
              </div>
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}

function SMRPerformance({ data: SMR_DATA, period }) {
  const smr = SMR_DATA || DEFAULT_SMR
  const rag = smr.overdue > 0 ? 'red' : smr.filedLate > 2 ? 'amber' : 'green'
  const filedQ = Number(smr.filedThisQuarter) || 0
  const pctRate = filedQ > 0 ? Math.round((Number(smr.filedOnTime) / filedQ) * 100) : null
  const canAvgDays = filedQ > 0
  const activeN = Number(smr.currentlyActive) || 0
  return (
    <SectionCard title="Suspicious Matter Report Performance" subtitle={`${period} · AUSTRAC filing obligations`} rag={rag} icon={FileText}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="Filed this quarter" value={smr.filedThisQuarter} color={C.pri} />
        <Stat label="Filed on time" value={smr.filedOnTime} color={C.green} sub={filedQ > 0 ? `${pctRate}% rate` : 'No filings this quarter'} />
        <Stat label="Filed late" value={smr.filedLate} color={smr.filedLate > 0 ? C.amber : C.green} sub={smr.filedLate > 0 ? 'Documented' : ''} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 14 }}>
        <div style={{ background: C.slateBg, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: canAvgDays ? C.pri : C.slateL, fontFamily: F.sans }}>{canAvgDays ? smr.avgDaysToFile : '—'}</div>
          <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>Avg days — suspicion to filing</div>
          <div style={{ fontSize: 12, color: canAvgDays ? C.green : C.slateL, fontWeight: 600, fontFamily: F.sans }}>
            {canAvgDays ? 'Threshold: 3 business days' : 'Not applicable until filings exist'}
          </div>
        </div>
        <div style={{ background: C.slateBg, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: smr.overdue > 0 ? C.red : C.green, fontFamily: F.sans }}>{smr.overdue}</div>
          <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>Currently overdue</div>
          <div style={{ fontSize: 12, color: activeN > 0 ? C.green : C.slateL, fontWeight: 600, fontFamily: F.sans }}>
            {activeN > 0 ? `All ${activeN} active within deadline` : 'No SMRs in progress'}
          </div>
        </div>
      </div>
      {smr.filedLate > 0 && smr.lateDetail && (
        <div style={{ padding: '9px 12px', background: C.amberBg, border: `1px solid ${C.amberBd}`, borderRadius: 7, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.amber, marginBottom: 2, fontFamily: F.sans }}>Late filing — {smr.lateDetail.id}</div>
          <div style={{ fontSize: 12, color: '#78350f', fontFamily: F.sans }}>{smr.lateDetail.reason} · {smr.lateDetail.dayslate} day late · Documented and resolved</div>
        </div>
      )}
    </SectionCard>
  )
}

function InvestigationHealth({ data: INV_DATA }) {
  const inv = INV_DATA || DEFAULT_INV
  const rag = inv.stalledCases > 2 ? 'red' : inv.stalledCases > 0 ? 'amber' : 'green'
  const openN = Number(inv.openCases) || 0
  const openCases = openN || 1
  const closedQ = Number(inv.closedThisQuarter) || 0
  const showAvgAge = openN > 0
  return (
    <SectionCard title="Investigation Pipeline Health" subtitle="Case progression and closure rates" rag={rag} icon={Activity}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        <Stat label="Open cases" value={inv.openCases} color={C.pri} />
        <Stat
          label="Avg age (days)"
          value={showAvgAge ? inv.avgAgeOpenDays : '—'}
          color={showAvgAge ? C.ink : C.slateL}
          sub={showAvgAge ? undefined : 'No open cases'}
        />
        <Stat label="Stalled cases" value={inv.stalledCases} color={inv.stalledCases > 0 ? C.amber : C.green} sub={inv.stalledCases > 0 ? '7+ days no movement' : ''} />
      </div>
      {inv.stalledCases > 0 && inv.stalledDetail && (
        <div style={{ padding: '9px 12px', background: C.amberBg, border: `1px solid ${C.amberBd}`, borderRadius: 7, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.amber, marginBottom: 1, fontFamily: F.sans }}>Stalled — {inv.stalledDetail.id}</div>
          <div style={{ fontSize: 12, color: '#78350f', fontFamily: F.sans }}>
            {inv.stalledDetail.patron ? <>Patron {inv.stalledDetail.patron} · </> : null}
            {inv.stalledDetail.stage}
            {typeof inv.stalledDetail.stageDays === 'number' && inv.stalledDetail.stageDays > 0
              ? <> · {inv.stalledDetail.stageDays} days since last update</>
              : null}
            {inv.stalledDetail.assignee ? <> · Assigned: {inv.stalledDetail.assignee}</> : <> · Unassigned</>}
          </div>
        </div>
      )}
      {inv.stageSplit?.length > 0 && (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 7, fontFamily: F.sans }}>Current stage split</div>
        {inv.stageSplit.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 5 }}>
            <div style={{ fontSize: 12, color: C.slate, width: 130, flexShrink: 0, fontFamily: F.sans }}>{s.stage}</div>
            <div style={{ flex: 1, height: 5, background: C.border, borderRadius: 3 }}>
              <div style={{ height: '100%', width: `${(s.count / openCases) * 100}%`, background: s.color, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: s.color, fontFamily: F.sans, width: 16, textAlign: 'right' }}>{s.count}</div>
          </div>
        ))}
      </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
        <div style={{ background: C.slateBg, borderRadius: 7, padding: '9px 11px' }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: C.green, fontFamily: F.sans }}>{inv.closedThisQuarter}</div>
          <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>Closed this quarter</div>
          <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>
            {closedQ > 0 ? `Avg ${inv.avgDaysToClose} days to close` : 'No closures in period — avg N/A'}
          </div>
        </div>
        <div style={{ background: C.slateBg, borderRadius: 7, padding: '9px 11px' }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: C.pri, fontFamily: F.sans }}>{inv.escalatedToSMR}</div>
          <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>Escalated to SMR</div>
          <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{inv.dismissedNoAction} dismissed, no action</div>
        </div>
      </div>
    </SectionCard>
  )
}

function PatronRisk({ data: PATRON_DATA }) {
  const patron = PATRON_DATA || EMPTY_PATRON
  const rag = patron.highRiskWithoutECDD > 10 ? 'red' : patron.highRiskWithoutECDD > 0 ? 'amber' : 'green'
  return (
    <SectionCard title="Patron Risk Profile" subtitle="Active member base — all venues" rag={rag} icon={Users}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <Stat label="Active patrons" value={patron.totalActive.toLocaleString()} color={C.pri} />
        <Stat label="On watchlist" value={patron.watchlistTotal} color={C.purple} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: F.sans }}>Risk distribution</div>
        {patron.riskBands.map((b, i) => (
          <div key={i} style={{ marginBottom: 7 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 14, color: C.ink, fontFamily: F.sans }}>{b.label}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: b.color, fontFamily: F.sans }}>{b.count.toLocaleString()} <span style={{ fontWeight: 400, color: C.slateL }}>({b.pct}%)</span></span>
            </div>
            <ProgressBar pct={b.pct} color={b.color} />
          </div>
        ))}
      </div>
      {patron.highRiskWithoutECDD > 0 && (
        <div style={{ padding: '9px 12px', background: C.amberBg, border: `1px solid ${C.amberBd}`, borderRadius: 7, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.amber, fontFamily: F.sans }}>{patron.highRiskWithoutECDD} high-risk patrons without active ECDD</div>
          <div style={{ fontSize: 12, color: '#78350f', marginTop: 2, fontFamily: F.sans }}>Enhanced customer due diligence required. Compliance team has been notified.</div>
        </div>
      )}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: F.sans }}>Exclusions</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div style={{ background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 7, padding: '9px 11px' }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: C.red, fontFamily: F.sans }}>{patron.venueInitiatedExclusions?.total ?? 0}</div>
            <div style={{ fontSize: 12, color: C.slate, fontFamily: F.sans }}>Venue-initiated exclusions</div>
            <div style={{ fontSize: 12, color: C.red, fontWeight: 600, fontFamily: F.sans }}>{patron.venueInitiatedExclusions?.thisQuarter ?? 0} this quarter</div>
          </div>
          <div style={{ background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 11px' }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: C.ink, fontFamily: F.sans }}>{patron.selfExclusions?.total ?? 0}</div>
            <div style={{ fontSize: 12, color: C.slate, fontFamily: F.sans }}>Self-exclusions on register</div>
            <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{patron.selfExclusions?.thisQuarter ?? 0} this quarter</div>
          </div>
        </div>
        {(patron.venueInitiatedExclusions?.list || []).map((e, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 0', borderBottom: i < (patron.venueInitiatedExclusions?.list || []).length - 1 ? `1px solid ${C.border}` : 'none' }}>
            <UserX size={12} color={C.red} style={{ marginTop: 1, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, fontFamily: F.sans }}>{e.patron} <span style={{ fontWeight: 400, color: C.slateL }}>· {e.venue}</span></div>
              <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{e.date} · {e.reason}</div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  )
}

function capPct(n) { return Math.min(100, Math.max(0, Number(n) || 0)) }

function TrainingCompliance({ data: TRAINING_DATA }) {
  const raw = TRAINING_DATA ?? EMPTY_TRAINING
  const training = {
    ...raw,
    overallCompliance: capPct(raw.overallCompliance),
    venues: (raw.venues || []).map(v => ({ ...v, pct: capPct(v.pct) })),
  }
  const sumVenueCompleted = (training.venues || []).reduce((s, v) => s + (v.completed || 0), 0)
  const sumVenueOverdue = (training.venues || []).reduce((s, v) => s + (v.overdue || 0), 0)
  const totals = training.slotTotals
  const completedShown = totals != null ? totals.completed : sumVenueCompleted
  const overdueShown = totals != null ? totals.overdueStaff : sumVenueOverdue
  const rag = training.overallCompliance < 85 ? 'red' : training.overallCompliance < 95 ? 'amber' : 'green'
  return (
    <SectionCard title="AML/CTF Training Compliance" subtitle="Staff training completion — all venues and roles" rag={rag} icon={BookOpen}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16, padding: '12px 14px', background: C.slateBg, borderRadius: 9 }}>
        <div>
          <div style={{ fontSize: 36, fontWeight: 900, color: rag === 'green' ? C.green : rag === 'amber' ? C.amber : C.red, fontFamily: F.sans, lineHeight: 1 }}>{training.overallCompliance}%</div>
          <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>Overall completion</div>
        </div>
        <div style={{ flex: 1 }}>
          <ProgressBar pct={training.overallCompliance} color={rag === 'green' ? C.green : rag === 'amber' ? C.amber : C.red} height={10} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
            <span style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{completedShown} completed</span>
            <span style={{ fontSize: 12, color: C.red, fontWeight: 600, fontFamily: F.sans }}>{overdueShown} overdue</span>
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: F.sans }}>By venue</div>
        {(training.venues || []).map((v, i) => {
          const vc = v.pct >= 95 ? C.green : v.pct >= 85 ? C.amber : C.red
          return (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 14, color: C.ink, fontWeight: 500, fontFamily: F.sans }}>{v.name}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {v.overdue > 0 && <span style={{ fontSize: 12, color: C.red, fontWeight: 700, fontFamily: F.sans }}>{v.overdue} overdue</span>}
                  <span style={{ fontSize: 14, fontWeight: 800, color: vc, fontFamily: F.sans }}>{v.pct}%</span>
                </div>
              </div>
              <ProgressBar pct={v.pct} color={vc} />
            </div>
          )
        })}
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: F.sans }}>Overdue by role</div>
        {(training.overdueBreakdown || []).map((r, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < (training.overdueBreakdown || []).length - 1 ? `1px solid ${C.border}` : 'none' }}>
            <span style={{ fontSize: 14, color: C.ink, fontFamily: F.sans }}>{r.role}</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{r.total} required</span>
              {r.overdue > 0
                ? <span style={{ fontSize: 12, fontWeight: 700, color: C.red, background: C.redBg, padding: '1px 8px', borderRadius: 10, border: `1px solid ${C.redBd}`, fontFamily: F.sans }}>{r.overdue} overdue</span>
                : <span style={{ fontSize: 12, fontWeight: 700, color: C.green, background: C.greenBg, padding: '1px 8px', borderRadius: 10, border: `1px solid ${C.greenBd}`, fontFamily: F.sans }}>All current</span>}
            </div>
          </div>
        ))}
        {training.nextDueWithin30Days > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: C.amber, fontWeight: 600, fontFamily: F.sans }}>⚠ {training.nextDueWithin30Days} renewals due within 30 days</div>
        )}
      </div>
    </SectionCard>
  )
}

function EmployeeDueDiligence({ data: EDD_DATA }) {
  const edd = EDD_DATA && typeof EDD_DATA === 'object' ? EDD_DATA : EMPTY_EDD
  const isEmpty = !edd.totalStaff && !(edd.byRole || []).length && !edd.eddCompleted
  const rag = isEmpty ? 'green' : edd.overdue > 5 ? 'red' : edd.overdue > 0 ? 'amber' : 'green'
  return (
    <SectionCard title="Employee Due Diligence" subtitle="Staff screening and KYC obligations" rag={rag} icon={Award}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16, padding: '12px 14px', background: C.slateBg, borderRadius: 9 }}>
        <div>
          <div style={{ fontSize: 36, fontWeight: 900, color: isEmpty ? C.slateL : rag === 'green' ? C.green : rag === 'amber' ? C.amber : C.red, fontFamily: F.sans, lineHeight: 1 }}>{isEmpty ? '—' : `${edd.eddPct}%`}</div>
          <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{isEmpty ? 'Not reported' : 'EDD completion'}</div>
        </div>
        <div style={{ flex: 1 }}>
          {!isEmpty && <ProgressBar pct={edd.eddPct} color={rag === 'green' ? C.green : C.amber} height={10} />}
          {isEmpty && <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>Employee due diligence aggregates will appear when the board API includes an <code style={{ background: C.border, padding: '1px 5px', borderRadius: 4 }}>edd</code> payload.</div>}
          {!isEmpty && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
            <span style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{edd.eddCompleted} of {edd.totalStaff} staff</span>
            {edd.expiringSoon > 0 && <span style={{ fontSize: 12, color: C.amber, fontWeight: 600, fontFamily: F.sans }}>{edd.expiringSoon} expiring soon</span>}
          </div>
          )}
        </div>
      </div>
      {edd.overdue > 0 && (
        <div style={{ padding: '9px 12px', background: C.amberBg, border: `1px solid ${C.amberBd}`, borderRadius: 7, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.amber, fontFamily: F.sans }}>{edd.overdue} staff with overdue EDD</div>
          <div style={{ fontSize: 12, color: '#78350f', marginTop: 1, fontFamily: F.sans }}>Compliance team has been notified. Resolution expected within 14 days.</div>
        </div>
      )}
      <div style={{ fontSize: 12, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: F.sans }}>By role</div>
      {(edd.byRole || []).length === 0 && isEmpty ? (
        <div style={{ fontSize: 13, color: C.slateL, fontFamily: F.sans }}>No role-level EDD breakdown.</div>
      ) : (
      (edd.byRole || []).map((r, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < (edd.byRole || []).length - 1 ? `1px solid ${C.border}` : 'none' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, fontFamily: F.sans }}>{r.role}</div>
            <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{r.completed}/{r.total} screened</div>
          </div>
          {(r.overdue || 0) > 0
            ? <span style={{ fontSize: 12, fontWeight: 700, color: C.red, background: C.redBg, padding: '2px 9px', borderRadius: 10, border: `1px solid ${C.redBd}`, fontFamily: F.sans }}>{r.overdue} overdue</span>
            : <CheckCircle2 size={15} color={C.green} />}
        </div>
      ))
      )}
    </SectionCard>
  )
}

function VenueRiskRatings({ data: VENUE_RISK }) {
  const venues = Array.isArray(VENUE_RISK) ? VENUE_RISK : EMPTY_VENUE_RISK
  const hasHigh = venues.some(v => v.rating === 'High')
  const rag = venues.length === 0 ? 'green' : hasHigh ? 'amber' : 'green'
  const ratingColor = (r) => r === 'High' ? C.red : r === 'Medium' ? C.amber : C.green
  const ratingBg = (r) => r === 'High' ? C.redBg : r === 'Medium' ? C.amberBg : C.greenBg
  const ratingBd = (r) => r === 'High' ? C.redBd : r === 'Medium' ? C.amberBd : C.greenBd
  return (
    <SectionCard title="Venue Risk Ratings" subtitle="Inherent risk assessment — all venues" rag={rag} icon={Building2} span={2}>
      {venues.length === 0 ? (
        <div style={{ fontSize: 13, color: C.slateL, fontFamily: F.sans }}>No venue risk rows yet — seed governance risks or add venues.</div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {venues.map((v, i) => (
          <div key={v.id || `venue-risk-${i}`} style={{ background: ratingBg(v.rating), border: `1px solid ${ratingBd(v.rating)}`, borderRadius: 9, padding: '14px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, lineHeight: 1.3, marginBottom: 3, fontFamily: F.sans }}>{v.name}</div>
                <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>{v.egms} EGMs</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 800, color: ratingColor(v.rating), background: '#fff', padding: '2px 8px', borderRadius: 7, border: `1px solid ${ratingBd(v.rating)}`, whiteSpace: 'nowrap', fontFamily: F.sans }}>{v.rating}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 8 }}>
              <div style={{ fontSize: 30, fontWeight: 900, color: ratingColor(v.rating), fontFamily: F.sans, lineHeight: 1 }}>{v.score}</div>
              <div style={{ fontSize: 12, color: C.slateL, fontFamily: F.sans }}>/100</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 4 }}>
                {v.change === 'up' && <TrendingUp size={12} color={C.red} />}
                {v.change === 'down' && <TrendingDown size={12} color={C.green} />}
                {v.change === 'same' && <Minus size={12} color={C.slateL} />}
                <span style={{ fontSize: 12, color: v.change === 'up' ? C.red : v.change === 'down' ? C.green : C.slateL, fontWeight: 600, fontFamily: F.sans }}>
                  {v.change === 'up' ? `+${v.score - v.prev}` : v.change === 'down' ? `-${v.prev - v.score}` : 'No change'} vs Q4
                </span>
              </div>
            </div>
            {(v.flags || []).length > 0 && (
              <div>
                {(v.flags || []).map((f, j) => (
                  <div key={j} style={{ display: 'flex', gap: 5, alignItems: 'flex-start', marginBottom: 3 }}>
                    <AlertCircle size={9} color={C.amber} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#78350f', fontFamily: F.sans }}>{f}</span>
                  </div>
                ))}
              </div>
            )}
            {(v.flags || []).length === 0 && <div style={{ fontSize: 12, color: C.green, fontWeight: 600, fontFamily: F.sans }}>No flags this quarter</div>}
          </div>
        ))}
      </div>
      )}
    </SectionCard>
  )
}

/**
 * Compliance Intelligence Strip — I12 board surface integration.
 * Reads the four P1-item stats endpoints (risk register, TTR,
 * rules rationale, CDD tiers) via useGovernanceStats. Sits below
 * the executive KPI strip so the original board-dashboard numbers
 * (SMR on-time, stalled cases, training, EDD) are untouched.
 */
function ComplianceIntelligenceStrip({ stats }) {
  const riskOpen = riskOpenCount(stats)
  const riskOverdue = Number(stats?.riskRegister?.overdue_reviews || 0)
  const ttrOpen = ttrOpenCount(stats)
  const ttrOverdue = Number(stats?.ttrRegistry?.overdue_drafts || 0)
  const ttrFiled7d = Number(stats?.ttrRegistry?.filed_last_7_days || 0)
  const rulesNeedReview = Number(stats?.rules?.published_needing_review || 0)
  const rulesBackfill = Number(stats?.rules?.with_backfill_rationale || 0)
  const enhanced = cddTierCount(stats, 'enhanced')
  const prohibited = cddTierCount(stats, 'prohibited')
  const tierLocked = Number(stats?.cddTier?.total_locked || 0)

  const items = [
    {
      label: 'Risk register open',
      value: riskOpen,
      target: riskOverdue > 0 ? `${riskOverdue} overdue review${riskOverdue === 1 ? '' : 's'}` : 'Reviews current',
      rag: riskOverdue > 0 ? 'amber' : riskOpen === 0 ? 'green' : 'green',
      icon: AlertTriangle,
    },
    {
      label: 'TTRs to file',
      value: ttrOpen,
      target: ttrOverdue > 0 ? `${ttrOverdue} overdue` : `${ttrFiled7d} filed last 7d`,
      rag: ttrOverdue > 0 ? 'red' : ttrOpen > 0 ? 'amber' : 'green',
      icon: Banknote,
    },
    {
      label: 'Rules unreviewed',
      value: rulesNeedReview,
      target: rulesBackfill > 0 ? `${rulesBackfill} on backfill rationale` : 'All rules have rationale',
      rag: rulesNeedReview > 0 ? 'amber' : 'green',
      icon: HelpCircle,
    },
    {
      label: 'Enhanced / prohibited CDD',
      value: enhanced + prohibited,
      target: prohibited > 0
        ? `${prohibited} prohibited · ${tierLocked} locked`
        : `${enhanced} enhanced · ${tierLocked} locked`,
      rag: prohibited > 0 ? 'red' : enhanced > 0 ? 'amber' : 'green',
      icon: UserCog,
    },
  ]

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10, fontFamily: F.sans }}>
        Compliance intelligence — P1 data sources
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {items.map((item, i) => {
          const rc = item.rag === 'red' ? C.red : item.rag === 'amber' ? C.amber : C.green
          return (
            <div key={i} style={{ background: '#fff', border: `1px solid ${C.border}`, borderTop: `3px solid ${rc}`, borderRadius: 9, padding: '12px 14px' }}>
              <item.icon size={13} color={rc} style={{ marginBottom: 7 }} />
              <div style={{ fontSize: 20, fontWeight: 900, color: rc, fontFamily: F.sans, lineHeight: 1, marginBottom: 2 }}>{item.value}</div>
              <div style={{ fontSize: 12, color: C.slateL, lineHeight: 1.3, fontFamily: F.sans }}>{item.label}</div>
              {item.target && <div style={{ fontSize: 11, color: C.slateL, marginTop: 2, fontFamily: F.sans, opacity: 0.9 }}>{item.target}</div>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function BoardSummaryStrip({ smr, investigation, patron, training, edd }) {
  const smrD = smr || DEFAULT_SMR
  const invD = investigation || DEFAULT_INV
  const patronD = patron || EMPTY_PATRON
  const trainD = training ?? EMPTY_TRAINING
  const eddD = edd && typeof edd === 'object' ? edd : EMPTY_EDD
  const eddEmpty = !eddD.totalStaff && !(eddD.byRole || []).length && !eddD.eddCompleted
  const highRisk = (patronD.riskBands?.[2]?.count || 0) + (patronD.riskBands?.[3]?.count || 0)
  const smrHasFilings = Number(smrD.filedThisQuarter) > 0
  const smrPct = smrHasFilings ? Math.round((Number(smrD.filedOnTime) / Number(smrD.filedThisQuarter)) * 100) : null
  const items = [
    {
      label: 'SMRs filed on time',
      value: smrHasFilings ? `${smrPct}%` : 'N/A',
      target: smrHasFilings ? `Target: ≥${GOALS.smrOnTimePct}%` : 'No SMRs filed this quarter',
      rag: !smrHasFilings ? 'green' : smrPct >= GOALS.smrOnTimePct ? 'green' : smrPct >= 80 ? 'amber' : 'red',
      icon: FileText,
    },
    { label: 'Investigations open', value: invD.openCases, target: null, rag: 'green', icon: Activity },
    { label: 'Stalled cases', value: invD.stalledCases, target: `Target: ≤${GOALS.stalledCasesMax}`, rag: invD.stalledCases <= GOALS.stalledCasesMax ? 'green' : invD.stalledCases <= 2 ? 'amber' : 'red', icon: Clock },
    { label: 'High-risk patrons', value: highRisk, target: patronD.highRiskWithoutECDD > 0 ? `${patronD.highRiskWithoutECDD} without ECDD` : null, rag: patronD.highRiskWithoutECDD <= GOALS.highRiskWithoutEcddMax ? 'green' : patronD.highRiskWithoutECDD <= 5 ? 'amber' : 'red', icon: Users },
    { label: 'Venue exclusions (YTD)', value: patronD.venueInitiatedExclusions?.total ?? 0, target: null, rag: 'green', icon: UserX },
    { label: 'Training compliance', value: `${trainD.overallCompliance}%`, target: `Target: ≥${GOALS.trainingPct}%`, rag: trainD.overallCompliance >= GOALS.trainingPct ? 'green' : trainD.overallCompliance >= 85 ? 'amber' : 'red', icon: BookOpen },
    { label: 'EDD completion', value: eddEmpty ? 'N/A' : `${eddD.eddPct}%`, target: `Target: ${GOALS.eddPct}%`, rag: eddEmpty ? 'green' : eddD.eddPct >= GOALS.eddPct ? 'green' : eddD.overdue > 0 ? 'amber' : 'green', icon: Award },
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10, marginBottom: 24 }}>
      {items.map((item, i) => {
        const rc = item.rag === 'red' ? C.red : item.rag === 'amber' ? C.amber : C.green
        return (
          <div key={i} style={{ background: '#fff', border: `1px solid ${C.border}`, borderTop: `3px solid ${rc}`, borderRadius: 9, padding: '12px 14px' }}>
            <item.icon size={13} color={rc} style={{ marginBottom: 7 }} />
            <div style={{ fontSize: 20, fontWeight: 900, color: rc, fontFamily: F.sans, lineHeight: 1, marginBottom: 2 }}>{item.value}</div>
            <div style={{ fontSize: 12, color: C.slateL, lineHeight: 1.3, fontFamily: F.sans }}>{item.label}</div>
            {item.target && <div style={{ fontSize: 11, color: C.slateL, marginTop: 2, fontFamily: F.sans, opacity: 0.9 }}>{item.target}</div>}
          </div>
        )
      })}
    </div>
  )
}

const DEFAULT_PERIOD = (() => {
  const now = new Date()
  const q = Math.floor(now.getMonth() / 3) + 1
  return `Q${q} ${now.getFullYear()}`
})()

export default function BoardDashboard() {
  const [searchParams] = useSearchParams()
  const view = searchParams.get('view') === '1' ? '1' : '2'
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { data: govStats } = useGovernanceStats({ enabled: view === '1' && !loading && !error })

  useEffect(() => {
    if (view !== '1') return
    authFetch(getLocalApiUrl('/api/governance/board-dashboard'))
      .then(r => r.json())
      .then((d) => setData(d?.data ?? null))
      .catch(e => { setError(e?.message || 'Failed to load'); setData(null) })
      .finally(() => setLoading(false))
  }, [view])

  const period = data?.period ?? DEFAULT_PERIOD
  const smr = data?.smr ?? DEFAULT_SMR
  const investigation = data?.investigation ?? DEFAULT_INV
  const patron = data?.patron ?? EMPTY_PATRON
  const training = data ? trainingPayloadFromApi(data) : EMPTY_TRAINING
  const edd = data?.edd ?? EMPTY_EDD
  const venueRisk = data?.venueRisk ?? EMPTY_VENUE_RISK

  return (
    <div style={{ minHeight: '100%', fontFamily: F.sans, width: '100%' }}>
      {view === '2' && (
        <div style={{ padding: '12px 22px 22px' }}>
          <BoardDashboardPack />
        </div>
      )}
      {view === '1' && (
      <div style={{ padding: '22px' }}>
        {error && <div style={{ padding: 24, background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 8, color: C.red }}>{error}</div>}
        {!error && (
          <>
        {loading && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.slate, fontFamily: F.sans }}>
            Updating board figures…
          </div>
        )}
        <div style={{ marginBottom: 16, padding: '12px 16px', background: C.priM, border: `1px solid ${C.pri}33`, borderRadius: 8, fontFamily: F.sans }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.pri, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Compliance goals</div>
          <div style={{ fontSize: 13, color: C.ink }}>SMR on-time ≥95% · Stalled cases 0 · Training ≥95% · EDD 100% · High-risk with ECDD</div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10, fontFamily: F.sans }}>At a glance — key compliance indicators</div>
        <BoardSummaryStrip smr={smr} investigation={investigation} patron={patron} training={training} edd={edd} />

        <ComplianceIntelligenceStrip stats={govStats} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <VenueRiskRatings data={venueRisk} />
          <ProgramHealth />
          <SMRPerformance data={smr} period={period} />
          <InvestigationHealth data={investigation} />
          <PatronRisk data={patron} />
          <TrainingCompliance data={training} />
          <EmployeeDueDiligence data={edd} />
        </div>

          </>
        )}
      </div>
      )}
    </div>
  )
}
